import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import DOMPurify from 'dompurify';
import { Spinner } from '@klaro7/ui';
import { PROMO_RATE } from '@klaro7/shared-types';

import { StarRating } from '@/features/professionals/components/StarRating';
import { ReviewsSection } from '@/features/professionals/components/ReviewsSection';
import { usePublicProfessional } from '@/features/professionals/hooks/usePublicProfessionals';
import { resolveUploadUrl } from '@/lib/uploads';
import { getVideoEmbedUrl } from '@/lib/videoEmbed';
import type { CallType } from '@klaro7/shared-types';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useCall } from '@/features/calls/context/CallContext';
import { AvailabilityBadge } from '@/features/calls/components/AvailabilityBadge';
import { useAvailability } from '@/features/calls/hooks/useAvailability';
import { WorkingHoursNotice } from '@/features/professional-profile/components/WorkingHoursNotice';
import { FavoriteButton } from '@/features/favorites/components/FavoriteButton';
import { usePromoEligibility } from '@/features/wallet/hooks/useWallet';
import { useMyAvailabilityWatches } from '@/features/availability-watch/hooks/useAvailabilityWatch';
import { NotifyWhenOnlineButton } from '@/features/availability-watch/components/NotifyWhenOnlineButton';
import { ConsultantSeo } from '@/features/seo/components/ConsultantSeo';

export function ConsultantPage() {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { callState, initiateCall } = useCall();
  const { data: professional, isLoading, isError } = usePublicProfessional(id!);
  const { getStatus } = useAvailability();
  const { isPromoEligible } = usePromoEligibility();
  const { data: watchData } = useMyAvailabilityWatches(isAuthenticated);
  const [showCallOptions, setShowCallOptions] = useState(false);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-brand-navy">
        <Spinner size="lg" className="border-brand-orange/30 border-t-brand-orange" />
      </div>
    );
  }

  const user = professional?.userId;

  if (isError || !professional || !user?.profile) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center bg-brand-navy px-4 text-center">
        <p className="mb-4 text-lg text-white/60">Consulente non trovato</p>
        <Link
          to="/"
          className="rounded-lg border border-white/15 bg-white/5 px-6 py-2 text-sm text-white/70 transition-colors hover:bg-white/10"
        >
          Torna alla home
        </Link>
      </div>
    );
  }

  const fullName = `${user.profile.firstName ?? ''} ${user.profile.lastName ?? ''}`.trim();
  const onlineStatus = getStatus(professional.id, professional.onlineStatus);
  const isAvailable = onlineStatus === 'online';
  const isPaused = onlineStatus === 'paused';
  const isWatching = !!watchData?.watches?.some((w) => w.professionalId === professional.id);
  const videoEmbedUrl = professional.videoUrl ? getVideoEmbedUrl(professional.videoUrl) : null;

  const chatHref = isAuthenticated
    ? `/my/chat?professional=${professional.id}`
    : `/login?redirect=${encodeURIComponent(`/my/chat?professional=${professional.id}`)}`;

  return (
    <div className="min-h-screen bg-brand-navy text-white">
      <ConsultantSeo professional={professional} />
      {/* Background effects */}
      <div className="pointer-events-none fixed inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_0%,rgba(24,153,170,0.2)_0%,transparent_60%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_80%_50%,rgba(235,114,35,0.08)_0%,transparent_50%)]" />
      </div>

      {/* Back link */}
      <div className="relative z-10 mx-auto max-w-4xl px-4 pt-8">
        <Link
          to="/"
          className="inline-flex items-center gap-2 rounded-lg py-2 px-3 -ml-3 text-sm text-white/50 transition-colors hover:text-white/80 hover:bg-white/5"
        >
          <svg aria-hidden="true" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Torna ai consulenti
        </Link>
      </div>

      {/* Profile header */}
      {(() => {
        const audioEnabled = professional.serviceChannels?.audioCall !== false;
        const videoEnabled = professional.serviceChannels?.videoCall !== false;
        const chatEnabled = professional.serviceChannels?.chat !== false;
        const hasCallChannels = audioEnabled || videoEnabled;
        const hasBothCallTypes = audioEnabled && videoEnabled;
        const callLabel = hasBothCallTypes
          ? 'CHIAMA'
          : videoEnabled
            ? 'VIDEOCHIAMATA'
            : 'CHIAMATA AUDIO';
        const isCallDisabled = onlineStatus !== 'online' || callState !== 'idle' || !professional.costPerMinute;

        const handleCallType = (type: CallType) => {
          if (!isAuthenticated) {
            navigate('/login', { state: { from: location } });
            return;
          }
          setShowCallOptions(false);
          initiateCall(professional.id, type, fullName);
        };

        const handleCallClick = () => {
          if (isCallDisabled) return;
          if (!isAuthenticated) {
            navigate('/login', { state: { from: location } });
            return;
          }
          if (hasBothCallTypes) {
            setShowCallOptions(!showCallOptions);
          } else if (audioEnabled) {
            handleCallType('audio');
          } else {
            handleCallType('video');
          }
        };

        return (
          <div className="relative z-10 mx-auto max-w-4xl px-4 py-8">
            <div className="relative overflow-hidden rounded-2xl border border-indigo-400/15 bg-gradient-to-br from-[#2c2f5a]/90 to-[#23254b]/95 p-6 backdrop-blur-sm md:p-10">
              {/* Glow effect */}
              <div className="pointer-events-none absolute -right-24 -top-24 h-48 w-48 rounded-full bg-brand-orange/10 blur-3xl" />

              <div className="relative flex flex-col gap-6 md:flex-row md:items-start">
                {/* Avatar */}
                <div className="shrink-0 self-center md:self-start">
                  <div className="rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 p-1">
                    {user.profile?.avatar && !imgError ? (
                      <img
                        src={resolveUploadUrl(user.profile.avatar)}
                        alt={fullName}
                        className="h-36 w-36 rounded-full object-cover"
                        onError={() => setImgError(true)}
                      />
                    ) : (
                      <div className="flex h-36 w-36 items-center justify-center rounded-full bg-neutral-100">
                        <img src="/images/logo-navy.png" alt="" className="h-20 w-20 object-contain opacity-60" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Info + CTAs */}
                <div className="flex-1 text-center md:text-left">
                  {/* Top row: Name + Price */}
                  <div className="flex flex-col items-center md:flex-row md:items-start md:justify-between">
                    <div>
                      <div className="flex items-center gap-3">
                        <h1 className="text-3xl font-bold text-white">{fullName}</h1>
                        <FavoriteButton professionalId={professional.id} size="md" variant="dark" />
                      </div>
                      {professional.title && (
                        <p className="mt-0.5 text-lg text-white/50">{professional.title}</p>
                      )}
                    </div>
                    {/* Rate applies to paid chat too: show it for chat-only consultants */}
                    {professional.costPerMinute != null && (hasCallChannels || chatEnabled) && (
                      <div className="mt-2 text-center md:mt-0 md:text-right">
                        {isPromoEligible ? (
                          <>
                            <p className="text-sm font-medium text-white/40 line-through">
                              {professional.costPerMinute.toFixed(2)}&euro;
                            </p>
                            <p className="text-3xl font-bold text-brand-orange">
                              {PROMO_RATE.toFixed(2).replace('.', ',')}
                            </p>
                          </>
                        ) : (
                          <p className="text-3xl font-bold text-brand-orange">
                            {professional.costPerMinute.toFixed(2)}
                          </p>
                        )}
                        <p className="text-xs font-medium uppercase tracking-wider text-white/50">
                          {professional.currency || 'EUR'}/MIN
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Rating + Online status */}
                  <div className="mt-3 flex flex-wrap items-center justify-center gap-3 md:justify-start">
                    <StarRating
                      rating={professional.rating ?? 0}
                      reviewCount={professional.reviewCount}
                      size="md"
                    />
                    <span className="text-white/30">•</span>
                    <AvailabilityBadge status={onlineStatus} showLabel size="md" variant="dark" />
                  </div>

                  {/* Specialization pills */}
                  {professional.specializations.length > 0 && (
                    <div className="mt-3 flex flex-wrap justify-center gap-2 md:justify-start">
                      {professional.specializations.map((spec) => (
                        <span
                          key={spec}
                          className="rounded-full border border-white/10 bg-white/[0.08] px-4 py-1.5 text-sm text-white/70"
                        >
                          {spec}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* CTA buttons — online: Chiama (messaging blocked while online).
                      offline/busy/paused: Chat (leave an async message, hidden if paused) + "Avvisami" */}
                  {(hasCallChannels || chatEnabled) && (
                    <div className="mt-6 flex flex-wrap items-stretch justify-center gap-3 md:justify-start">
                      {!isAvailable ? (
                        <>
                          {chatEnabled && !isPaused && (
                            <Link
                              to={chatHref}
                              className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-indigo-300/30 bg-white/10 px-6 py-3 text-sm font-bold text-white/80 transition-all hover:border-indigo-300/50 hover:bg-white/15"
                            >
                              <svg aria-hidden="true" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                              </svg>
                              Chat
                            </Link>
                          )}
                          <NotifyWhenOnlineButton
                            professionalId={professional.id}
                            professionalName={fullName}
                            isWatching={isWatching}
                          />
                          {isPaused && (
                            <p className="w-full self-center text-sm text-white/60 md:w-auto">
                              Il consulente è in pausa: torna presto disponibile.
                            </p>
                          )}
                        </>
                      ) : (
                      <>
                      {/* Call button */}
                      {hasCallChannels && (
                        <div className="relative" onKeyDown={(e) => { if (e.key === 'Escape') setShowCallOptions(false); }}>
                          <button
                            onClick={handleCallClick}
                            disabled={isCallDisabled}
                            aria-haspopup={hasBothCallTypes ? 'true' : undefined}
                            aria-expanded={hasBothCallTypes ? showCallOptions : undefined}
                            className="flex items-center gap-3 rounded-xl bg-brand-orange px-5 py-3 font-bold text-white transition-all hover:bg-brand-orange/90 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {/* Phone icon in circle */}
                            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-white/20">
                              <svg aria-hidden="true" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                {videoEnabled && !audioEnabled ? (
                                  <path strokeLinecap="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
                                ) : (
                                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                                )}
                              </svg>
                            </span>
                            {/* Two-line label */}
                            <span className="text-left">
                              <span className="block text-[10px] uppercase tracking-wider text-white/80">{callLabel}</span>
                              <span className="block text-sm">Parla ora</span>
                            </span>
                            {/* Divider + Price */}
                            {professional.costPerMinute != null && (
                              <>
                                <span className="mx-1 h-8 w-px bg-white/30" />
                                <span className="text-center">
                                  {isPromoEligible ? (
                                    <>
                                      <span className="block text-[10px] text-white/60 line-through">{professional.costPerMinute.toFixed(2)}&euro;</span>
                                      <span className="block text-sm font-bold">{PROMO_RATE.toFixed(2).replace('.', ',')}&euro;</span>
                                    </>
                                  ) : (
                                    <span className="block text-sm font-bold">{professional.costPerMinute.toFixed(2)}&euro;</span>
                                  )}
                                  <span className="block text-[10px] text-white/70">/min</span>
                                </span>
                              </>
                            )}
                          </button>

                          {/* Dropdown for both call types */}
                          {showCallOptions && hasBothCallTypes && (
                            <>
                              <div role="presentation" className="fixed inset-0 z-40" onClick={() => setShowCallOptions(false)} />
                              <div role="menu" className="absolute bottom-full left-0 z-50 mb-2 w-52 rounded-xl border border-white/15 bg-brand-navy py-1 shadow-lg">
                                <button
                                  role="menuitem"
                                  onClick={() => handleCallType('audio')}
                                  className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-white/80 hover:bg-white/5"
                                >
                                  <svg aria-hidden="true" className="h-4 w-4 text-brand-teal" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
                                  </svg>
                                  Chiamata audio
                                </button>
                                <button
                                  role="menuitem"
                                  onClick={() => handleCallType('video')}
                                  className="flex w-full items-center gap-3 px-4 py-2.5 text-sm text-white/80 hover:bg-white/5"
                                >
                                  <svg aria-hidden="true" className="h-4 w-4 text-brand-teal" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                    <path strokeLinecap="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
                                  </svg>
                                  Videochiamata
                                </button>
                              </div>
                            </>
                          )}
                        </div>
                      )}

                      {/* Paid chat button (online only) */}
                      {chatEnabled && (
                        <button
                          onClick={() => handleCallType('chat')}
                          className="inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl border border-indigo-300/30 bg-white/10 px-6 py-3 text-sm font-bold text-white/80 transition-all hover:border-indigo-300/50 hover:bg-white/15"
                        >
                          <svg aria-hidden="true" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                          </svg>
                          Chat
                        </button>
                      )}

                      </>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Bottom decorative line */}
              <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-indigo-400/20 to-transparent" />
            </div>
          </div>
        );
      })()}

      {/* Video + Description */}
      <div className="relative z-10 mx-auto max-w-4xl px-4 pb-16">
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Video — YouTube/Vimeo embedded via iframe; a raw file URL falls
                back to a native <video> player. */}
            {professional.videoUrl && (
              <div className="overflow-hidden rounded-2xl border border-white/10 bg-white/5">
                <div className="aspect-video">
                  {videoEmbedUrl ? (
                    <iframe
                      src={videoEmbedUrl}
                      title="Video di presentazione"
                      className="h-full w-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  ) : (
                    <video
                      src={professional.videoUrl}
                      controls
                      className="h-full w-full object-cover"
                    />
                  )}
                </div>
              </div>
            )}

            {/* Description */}
            {professional.profileDescription && (
              <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
                <h2 className="mb-4 text-lg font-semibold text-white">
                  Chi sono
                </h2>
                <div
                  className="prose prose-invert prose-sm max-w-none text-white/70 prose-headings:text-white prose-a:text-brand-teal prose-strong:text-white"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(professional.profileDescription),
                  }}
                />
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Experience */}
            <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
              <h3 className="mb-4 text-lg font-semibold text-white">Dettagli</h3>
              <dl className="space-y-3">
                <div>
                  <dt className="text-xs uppercase tracking-wider text-white/40">
                    Esperienza
                  </dt>
                  <dd className="text-white/80">
                    {professional.experience}{' '}
                    {professional.experience === 1 ? 'anno' : 'anni'}
                  </dd>
                </div>
                {professional.education && (
                  <div>
                    <dt className="text-xs uppercase tracking-wider text-white/40">
                      Formazione
                    </dt>
                    <dd className="text-white/80">{professional.education}</dd>
                  </div>
                )}
              </dl>
            </div>

            {/* Certifications */}
            {professional.certifications &&
              professional.certifications.length > 0 && (
                <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
                  <h3 className="mb-4 text-lg font-semibold text-white">
                    Certificazioni
                  </h3>
                  <ul className="space-y-2">
                    {professional.certifications.map((cert, i) => (
                      <li
                        key={i}
                        className="flex items-start gap-2 text-sm text-white/70"
                      >
                        <svg
                          aria-hidden="true"
                          className="mt-0.5 h-4 w-4 shrink-0 text-brand-orange"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M5 13l4 4L19 7"
                          />
                        </svg>
                        {cert}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

            {/* Working Hours */}
            {professional.availability?.workingHours &&
              professional.availability.workingHours.length > 0 && (
                <div className="rounded-2xl border border-white/10 bg-white/5 p-6">
                  <h3 className="mb-4 text-lg font-semibold text-white">
                    Orari di lavoro
                  </h3>
                  <ul className="space-y-1.5 text-sm text-white/70">
                    {professional.availability.workingHours.map((wh, i) => {
                      const days = [
                        'Domenica',
                        'Lunedi',
                        'Martedi',
                        'Mercoledi',
                        'Giovedi',
                        'Venerdi',
                        'Sabato',
                      ];
                      return (
                        <li key={i} className="flex justify-between">
                          <span>{days[wh.dayOfWeek]}</span>
                          <span className="text-white/50">
                            {wh.startTime} - {wh.endTime}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                  <WorkingHoursNotice variant="public" className="mt-3" />
                  {professional.availability.notes && (
                    <p className="mt-3 border-t border-white/10 pt-3 text-sm italic text-white/60">
                      {professional.availability.notes}
                    </p>
                  )}
                </div>
              )}
          </div>
        </div>

        {/* Reviews */}
        {typeof professional.reviewCount === 'number' && professional.reviewCount > 0 && (
          <div className="mt-6">
            <ReviewsSection
              professionalId={professional.id}
              professionalName={fullName}
              rating={professional.rating ?? 0}
              reviewCount={professional.reviewCount}
            />
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/10 px-4 py-8">
        <div className="mx-auto max-w-6xl text-center">
          <p className="text-sm text-white/30">
            Klaro7 - Consulenza di Cartomanzia Online
          </p>
        </div>
      </footer>

    </div>
  );
}
